<script>
import { formatDateTimeShortBR } from "@src/helpers/date";
import Api from "@services/API";
import ClockIcon from "@src/components/ui-design/icons/ClockIcon.vue";
import SaleIcon from "@src/components/ui-design/icons/SaleIcon.vue";
import CheckIcon from "@src/components/ui-design/icons/CheckIcon.vue";
import RefundIcon from "@src/components/ui-design/icons/RefundIcon.vue";
import WarningIcon from "@src/components/ui-design/icons/WarningIcon.vue";
import Package from "@src/components/ui-design/icons/Package.vue";

const GATEWAY_ATTEMPT_TYPE = "gateway_attempt";
const PAYMENT_APPROVED_TYPE = "payment_approved";
const SALE_CREATED_TYPE = "sale_created";
const GATEWAY_ACTION_CARD = "CARD";

// The API sends gateway names uppercased: WORLDPAY, MERCADO_PAGO.
function formatGatewayName(gateway) {
  return String(gateway)
    .toLowerCase()
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

const TYPE_ICONS = {
  sale_created: SaleIcon,
  fingerprint_captured: CheckIcon,
  konduto_analysis: CheckIcon,
  payment_approved: CheckIcon,
  payment_refused: WarningIcon,
  invoice_generated: Package,
  dispute_opened: WarningIcon,
  refund_requested: RefundIcon,
  refunded: RefundIcon,
  chargeback_opened: WarningIcon,
};

// A cor é definida pelo `status` enviado pelo back, não pelo tipo do evento.
// Assim o front acompanha automaticamente o status real de fingerprint, Konduto, etc.
const STATUS_STYLES = {
  success: { bg: "#E1F4F3", color: "#005952" },
  warning: { bg: "#FEFCE8", color: "#E6AF0C" },
  critical: { bg: "#FDECEA", color: "#C71307" },
  fail: { bg: "#FFF3ED", color: "#C71307" },
  default: { bg: "#F1F1F1", color: "#6C757D" },
};

export default {
  props: {
    saleId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      ClockIcon,
      timeline: [],
      loading: false,
      lastTimelineRequestId: 0,
    };
  },
  computed: {
    timelineItems() {
      return this.timeline.map((item, index) => {
        const nextItem = this.timeline[index + 1];
        const style = this.styleForItem(item, nextItem);

        return {
          ...item,
          event: this.labelForItem(item, nextItem),
          reason: this.reasonForItem(item, nextItem),
          bgColor: style.bg,
          iconColor: style.color,
        };
      });
    },
  },
  watch: {
    saleId() {
      this.fetchTimeline();
    },
  },
  mounted() {
    this.fetchTimeline();
  },
  methods: {
    iconForType(type) {
      return TYPE_ICONS[type] || ClockIcon;
    },
    styleForStatus(status) {
      return STATUS_STYLES[status] || STATUS_STYLES.default;
    },
    formatOccurredAt(iso) {
      if (!iso) return "";
      return formatDateTimeShortBR(iso);
    },
    // The API sends one attempt per action (CARD, then CHARGE) for each
    // gateway; only the outcome matters here. Attempts with no gateway are left
    // alone — grouping them would collapse unrelated events.
    dedupeGatewayAttempts(events) {
      const lastIndexByGateway = new Map();

      events.forEach((event, index) => {
        if (event.type !== GATEWAY_ATTEMPT_TYPE) return;

        const gateway = event.metadata && event.metadata.gateway;
        if (!gateway) return;

        lastIndexByGateway.set(gateway, index);
      });

      return events.filter((event, index) => {
        if (event.type !== GATEWAY_ATTEMPT_TYPE) return true;

        const gateway = event.metadata && event.metadata.gateway;
        if (!gateway) return true;

        return lastIndexByGateway.get(gateway) === index;
      });
    },
    // Gateway attempts carry no `sale_status`, so position is what tells the
    // outcome: the attempt right before `payment_approved` is the one that
    // settled the charge.
    isSettlingAttempt(item, nextItem) {
      return (
        item.type === GATEWAY_ATTEMPT_TYPE &&
        !!nextItem &&
        nextItem.type === PAYMENT_APPROVED_TYPE
      );
    },
    // Attempts that did not settle the charge are flagged red even when their
    // own status is `success` — succeeding alone does not mean the sale was paid.
    styleForItem(item, nextItem) {
      if (item.type !== GATEWAY_ATTEMPT_TYPE) {
        return this.styleForStatus(item.status);
      }

      if (this.isSettlingAttempt(item, nextItem)) {
        return this.styleForStatus(item.status);
      }

      return STATUS_STYLES.critical;
    },
    // Shares the rule above so a success wording never lands on a red icon.
    // For attempts that did not settle, the action tells where it stopped:
    // ending on CARD means tokenization blocked it before any charge.
    labelForItem(item, nextItem) {
      if (item.type !== GATEWAY_ATTEMPT_TYPE || !item.gateway) {
        return item.label;
      }

      const gateway = formatGatewayName(item.gateway);

      if (this.isSettlingAttempt(item, nextItem)) {
        return `Transação aprovada (${gateway})`;
      }

      if (item.action === GATEWAY_ACTION_CARD) {
        return `Erro na tokenização do cartão (${gateway})`;
      }

      return `Falha na transação (${gateway})`;
    },
    // The reason only explains a failure, so the attempt that settled the
    // charge never shows one, even if the API sent a stale text.
    reasonForItem(item, nextItem) {
      if (item.type !== GATEWAY_ATTEMPT_TYPE || this.isSettlingAttempt(item, nextItem)) {
        return "";
      }

      return item.failureReason || "";
    },
    // The API orders by `occurred_at` and the sale row is written after the
    // gateway attempts, which drops "sale created" into the middle of the list.
    moveSaleCreatedFirst(events) {
      const index = events.findIndex((event) => event.type === SALE_CREATED_TYPE);
      if (index <= 0) return events;

      const reordered = [...events];
      const [saleCreated] = reordered.splice(index, 1);

      return [saleCreated, ...reordered];
    },
    mapApiEvent(event) {
      const metadata = event.metadata || {};

      return {
        id: event.id,
        type: event.type || "",
        status: event.status,
        action: metadata.action || "",
        gateway: metadata.gateway || "",
        icon: this.iconForType(event.type || ""),
        date: this.formatOccurredAt(event.occurred_at),
        label: event.label || "",
        failureReason: metadata.failure_reason || "",
      };
    },
    async fetchTimeline() {
      const requestId = ++this.lastTimelineRequestId;

      if (this.saleId == null || this.saleId === "") {
        if (requestId !== this.lastTimelineRequestId) return;
        this.loading = false;
        this.timeline = [];
        return;
      }

      this.loading = true;

      try {
        const api = Api.build("sales");
        const data = await api.get(`/${this.saleId}/timeline`);
        const items = (data && data.timeline) || [];

        if (requestId !== this.lastTimelineRequestId) return;

        const ordered = this.moveSaleCreatedFirst(this.dedupeGatewayAttempts(items));

        this.timeline = ordered.map((e) => this.mapApiEvent(e));
      } catch (_) {
        if (requestId !== this.lastTimelineRequestId) return;

        this.timeline = [];
        this.$bvToast.toast("Não foi possível carregar a linha do tempo da venda.", {
          title: "Ops, algum erro aconteceu",
          variant: "danger",
          solid: true,
          autoHideDelay: 2000,
        });
      } finally {
        if (requestId === this.lastTimelineRequestId) {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<template>
  <div class="details-timeline card-box p-3 px-0">
    <h6 class="sales-details__subtitle">
      <component :is="ClockIcon" style="width: 15px; height: 15px"/>
      Linha do tempo
    </h6>
    <div v-if="loading" class="px-3 text-muted small">Carregando…</div>
    <p v-else-if="timelineItems.length === 0" class="px-3 text-muted small mb-0">
      Nenhum evento encontrado.
    </p>
    <div
      v-else
      style="display:flex; flex-direction: column; gap: 40px;"
    >
      <div
        v-for="(item, index) in timelineItems"
        :key="item.id"
        class="details-timeline__row d-flex align-items-start"
        :class="{ 'details-timeline__row--last': index === timelineItems.length - 1 }"
        style="gap: 14px"
      >
        <div
          :style="{ backgroundColor: item.bgColor, color: item.iconColor }"
          class="details-timeline__item"
        >
          <component :is="item.icon" />
        </div>
        <div>
          <p class="mb-0">{{ item.event }}</p>
          <p v-if="item.reason" class="details-timeline__reason small mb-0">
            Motivo: {{ item.reason }}
          </p>
          <p class="text-muted small mb-0">{{ item.date }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@src/design/ui-design/_variables.scss';

.details-timeline {
  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 5px;
    border-radius: 50%;
    flex-shrink: 0;

    svg {
      width: 14px!important;
      height: 14px!important;
    }
  }

  // The connector hangs from the row, not from the icon, so it stretches with
  // rows that grow when a reason wraps. It runs from 7px below this icon to
  // 7px above the next one, across the 40px gap between rows.
  &__row {
    position: relative;

    &::after {
      content: "";
      position: absolute;
      left: 14.5px;
      top: 37px;
      bottom: -33px;
      width: 1px;
      background-color: #E1F4F3;
    }

    &--last::after {
      display: none;
    }
  }

  &__reason {
    color: $danger;
    overflow-wrap: anywhere;
  }

}
</style>
